import { useEffect, useRef, useCallback } from 'react';

interface SwipeNavigationOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  enabled?: boolean;
  threshold?: number; // minimum swipe distance in px
  velocityThreshold?: number; // minimum swipe speed (px/ms)
}

export function useSwipeNavigation({
  onSwipeLeft,
  onSwipeRight,
  enabled = true,
  threshold = 50,
  velocityThreshold = 0.3,
}: SwipeNavigationOptions) {
  const touchStart = useRef<{ x: number; y: number; time: number } | null>(null);
  const touchEnd = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    // Don't intercept touches on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('input, textarea, select, button, [data-event-bar], [data-week-event], [role="dialog"]')) return;

    touchEnd.current = null;
    touchStart.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
      time: Date.now(),
    };
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    touchEnd.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    };
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart.current || !touchEnd.current) return;

    const dx = touchStart.current.x - touchEnd.current.x;
    const dy = touchStart.current.y - touchEnd.current.y;
    const dt = Date.now() - touchStart.current.time;
    const velocity = Math.abs(dx) / dt;

    // Only trigger if horizontal swipe is dominant (not vertical scroll)
    const isHorizontal = Math.abs(dx) > Math.abs(dy) * 1.5;

    if (isHorizontal && Math.abs(dx) > threshold && velocity > velocityThreshold) {
      if (dx > 0) {
        onSwipeLeft?.(); // swipe left = next
      } else {
        onSwipeRight?.(); // swipe right = prev
      }
    }

    touchStart.current = null;
    touchEnd.current = null;
  }, [onSwipeLeft, onSwipeRight, threshold, velocityThreshold]);

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);
}
